/**
 * Besedila strani modula Projekti (docs/ARHITEKTURA.md, 15.5) v slovenscini
 * in anglescini. Locen slovar, ker so strani modula samostojne in se
 * vklopijo samo pri nekaterih podjetjih; jezik je isti kot v vmesniku.
 */
import type { Jezik, PogledProjektov } from '@koda/shared';

import { useJezik } from './jezik';

export interface BesedilaProjektov {
  pogledi: Record<PogledProjektov, string>;
  meseci: string[];
  projekt: string;
  brezProjekta: string;
  skupaj: string;
  predvideno: string;
  porabljeno: string;
  preostanek: string;
  naMesec: string;
  zadrzano: string;
  zadrzanoNamig: string;
  mesecevDoPorabe: string;
  zakljucek: string;
  zacetnoStanje: (znesek: string, mesec: string) => string;
  zakljucen: string;
  pregledUvod: (mesec: string) => string;
  temeljnicaOdprta: (mesec: string) => string;
  niPodatkov: string;
  niProjektov: string;
  leto: string;
  mesec: string;
  prej: string;
  porabaUvod: string;
  odprtMesec: (mesec: string) => string;
  knjizbe: (opis: string) => string;
  brezKnjizb: string;
  datum: string;
  konto: string;
  vrsta: string;
  dokument: string;
  nosilec: string;
  znesek: string;
  opomba: string;
  zapri: string;
  sredstvo: string;
  naziv: string;
  delez: string;
  amortizacija: string;
  poOdstotkih: string;
  knjizeno: string;
  ostaliProjekti: string;
  poProjektihUvod: string;
  niSredstev: string;
  berem: string;
  // Nastavitev odstotkov
  odstotkiNaslov: string;
  odstotkiUvod: (najvec: number) => string;
  sredstvaIzRegistra: (mesec: string) => string;
  niRegistra: string;
  niOdprtihProjektov: string;
  invSt: string;
  neodpisana: string;
  mesecnaAmortizacija: string;
  nerazporejeno: string;
  veljaOd: string;
  spremembaOd: (mesec: string) => string;
  uredi: string;
  zgodovina: string;
  shrani: string;
  preklici: string;
  odstrani: string;
  odstotekProjekta: (projekt: string) => string;
  vsotaNad100: (vsota: string) => string;
  zgodovinaNaslov: string;
  brezRazdelitve: string;
  vseNerazporejeno: string;
  od: string;
  neznan: string;
  potrdiOdstranitev: (mesec: string) => string;
  napakaOdstotka: string;
  napakaBranja: string;
  zakljucenoDo: (mesec: string, odprt: string) => string;
}

const SL: BesedilaProjektov = {
  pogledi: { pregled: 'Pregled', 'po-projektih': 'Po projektih', poraba: 'Poraba', odstotki: 'Nastavitev odstotkov' },
  meseci: ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'avg', 'sep', 'okt', 'nov', 'dec'],
  projekt: 'projekt',
  brezProjekta: 'Brez projekta',
  skupaj: 'Skupaj',
  predvideno: 'predvidena amortizacija',
  porabljeno: 'porabljeno',
  preostanek: 'preostanek',
  naMesec: 'na mesec zdaj',
  zadrzano: 'zadržano',
  zadrzanoNamig: 'Sredstvo projekta zahteva preverjanje; razlog je v opozorilu.',
  mesecevDoPorabe: 'mesecev do porabe',
  zakljucek: 'zaključek',
  zacetnoStanje: (z, m) => `vključno z začetnim stanjem ${z} do ${m}`,
  zakljucen: 'zaključen',
  pregledUvod: (m) =>
    `Amortizacija, pripisana projektom (knjiženo na stroškovni nosilec projekta in začetno stanje). Poraba na mesec je po odstotkih za ${m}. Klik na znesek odpre podrobnosti.`,
  temeljnicaOdprta: (m) => `Za ${m} temeljnica razdelitve še ni knjižena; zneski tega meseca se bodo po knjiženju spremenili.`,
  niPodatkov: 'Ni še podatkov o amortizaciji.',
  niProjektov: 'Podjetje še nima projektov.',
  leto: 'Leto',
  mesec: 'Mesec',
  prej: 'prej',
  porabaUvod: 'Amortizacija, pripisana projektom po mesecih. »Prej« zajema začetno stanje in pretekla leta. Klik na znesek odpre knjižbe.',
  odprtMesec: (m) => `* ${m}: temeljnica še ni knjižena.`,
  knjizbe: (o) => `Knjižbe: ${o}`,
  brezKnjizb: 'Ni knjižb (mesec je v začetnem stanju ali brez amortizacije).',
  datum: 'datum',
  konto: 'konto',
  vrsta: 'vrsta',
  dokument: 'dokument',
  nosilec: 'nosilec',
  znesek: 'znesek',
  opomba: 'opomba',
  zapri: 'zapri',
  sredstvo: 'inv. št.',
  naziv: 'naziv',
  delez: 'delež',
  amortizacija: 'amortizacija',
  poOdstotkih: 'po odstotkih',
  knjizeno: 'knjiženo',
  ostaliProjekti: 'ostali projekti',
  poProjektihUvod: 'Sredstva izbranega projekta v mesecu: delež, kar projektu pripada po odstotkih, in kar mu je knjiženo.',
  niSredstev: 'Projekt v tem mesecu nima sredstev.',
  berem: 'Berem …',
  odstotkiNaslov: 'Nastavitev odstotkov projektov',
  odstotkiUvod: (n) =>
    `Delež amortizacije vsakega sredstva po projektih. Sprememba velja od izbranega meseca naprej; pretekli meseci ostanejo, kot so bili. Kar do 100 % manjka, je nerazporejeno. Na sredstvu je lahko največ ${String(n)} projektov.`,
  sredstvaIzRegistra: (m) => `Sredstva iz registra ${m}.`,
  niRegistra: 'Za ta mesec še ni naloženega registra osnovnih sredstev. Naloži ga računovodja na strani Uvoz.',
  niOdprtihProjektov: 'Podjetje še nima odprtih projektov. Vnese jih računovodja v šifrant projektov.',
  invSt: 'inv. št.',
  neodpisana: 'neodpisana vrednost',
  mesecnaAmortizacija: 'mesečna amortizacija',
  nerazporejeno: 'nerazporejeno',
  veljaOd: 'velja od',
  spremembaOd: (m) => `(sprememba od ${m})`,
  uredi: 'uredi',
  zgodovina: 'zgodovina',
  shrani: 'Shrani',
  preklici: 'prekliči',
  odstrani: 'odstrani',
  odstotekProjekta: (p) => `Odstotek ${p}`,
  vsotaNad100: (v) => `Vsota deležev je ${v}; največ je 100 %.`,
  zgodovinaNaslov: 'Zgodovina',
  brezRazdelitve: 'Sredstvo še nima razdelitve; vse je nerazporejeno.',
  vseNerazporejeno: 'vse nerazporejeno',
  od: 'od',
  neznan: 'neznan',
  potrdiOdstranitev: (m) => `Odstranim razdelitev, ki velja od ${m}? Spet bo veljala prejšnja.`,
  napakaOdstotka: 'Odstotek mora biti število med 0 in 100 z največ štirimi decimalkami, npr. 33,3333.',
  napakaBranja: 'Podatkov ni bilo mogoče prebrati.',
  zakljucenoDo: (m, o) => `Zaključeno do ${m}: odstotki do vključno tega meseca so zamrznjeni, sprememba lahko velja od ${o}.`,
};

const EN: BesedilaProjektov = {
  pogledi: { pregled: 'Overview', 'po-projektih': 'By project', poraba: 'Usage', odstotki: 'Project percentages' },
  meseci: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  projekt: 'project',
  brezProjekta: 'No project',
  skupaj: 'Total',
  predvideno: 'planned depreciation',
  porabljeno: 'used',
  preostanek: 'remaining',
  naMesec: 'per month now',
  zadrzano: 'on hold',
  zadrzanoNamig: 'An asset of this project needs checking; see the warning for the reason.',
  mesecevDoPorabe: 'months until used up',
  zakljucek: 'end date',
  zacetnoStanje: (z, m) => `including opening usage ${z} up to ${m}`,
  zakljucen: 'closed',
  pregledUvod: (m) =>
    `Depreciation attributed to projects (posted to the project's cost centre plus opening usage). Usage per month uses the percentages for ${m}. Click an amount for details.`,
  temeljnicaOdprta: (m) => `The allocation journal for ${m} is not posted yet; this month's amounts will change once it is.`,
  niPodatkov: 'No depreciation data yet.',
  niProjektov: 'The company has no projects yet.',
  leto: 'Year',
  mesec: 'Month',
  prej: 'before',
  porabaUvod: 'Depreciation attributed to projects by month. "Before" includes opening usage and earlier years. Click an amount to see the postings.',
  odprtMesec: (m) => `* ${m}: allocation journal not posted yet.`,
  knjizbe: (o) => `Postings: ${o}`,
  brezKnjizb: 'No postings (the month is covered by opening usage or has no depreciation).',
  datum: 'date',
  konto: 'account',
  vrsta: 'type',
  dokument: 'document',
  nosilec: 'cost centre',
  znesek: 'amount',
  opomba: 'note',
  zapri: 'close',
  sredstvo: 'asset no.',
  naziv: 'name',
  delez: 'share',
  amortizacija: 'depreciation',
  poOdstotkih: 'by percentages',
  knjizeno: 'posted',
  ostaliProjekti: 'other projects',
  poProjektihUvod: "Assets of the selected project in the month: share, the project's amount by percentages, and what is posted to it.",
  niSredstev: 'The project has no assets in this month.',
  berem: 'Loading …',
  odstotkiNaslov: 'Project percentages',
  odstotkiUvod: (n) =>
    `Share of each asset's depreciation by project. A change applies from the selected month onward; earlier months stay as they were. Whatever is missing to 100 % is unallocated. An asset can have at most ${String(n)} projects.`,
  sredstvaIzRegistra: (m) => `Assets from the register of ${m}.`,
  niRegistra: 'No fixed asset register has been uploaded for this month yet. The accountant uploads it on the Import page.',
  niOdprtihProjektov: 'The company has no open projects yet. The accountant enters them in the project list.',
  invSt: 'asset no.',
  neodpisana: 'net book value',
  mesecnaAmortizacija: 'monthly depreciation',
  nerazporejeno: 'unallocated',
  veljaOd: 'valid from',
  spremembaOd: (m) => `(change from ${m})`,
  uredi: 'edit',
  zgodovina: 'history',
  shrani: 'Save',
  preklici: 'cancel',
  odstrani: 'remove',
  odstotekProjekta: (p) => `Percentage ${p}`,
  vsotaNad100: (v) => `Shares add up to ${v}; the maximum is 100 %.`,
  zgodovinaNaslov: 'History',
  brezRazdelitve: 'The asset has no allocation yet; everything is unallocated.',
  vseNerazporejeno: 'all unallocated',
  od: 'from',
  neznan: 'unknown',
  potrdiOdstranitev: (m) => `Remove the allocation valid from ${m}? The previous one will apply again.`,
  napakaOdstotka: 'A percentage must be a number between 0 and 100 with at most four decimals, e.g. 33,3333.',
  napakaBranja: 'The data could not be loaded.',
  zakljucenoDo: (m, o) => `Closed up to ${m}: percentages up to and including that month are frozen; a change can apply from ${o}.`,
};

const SLOVARJA: Record<Jezik, BesedilaProjektov> = { sl: SL, en: EN };

export function useBesedilaProjektov(): BesedilaProjektov {
  return SLOVARJA[useJezik()];
}

/** Mesec `llll-mm` v zapis 09/2026. */
export function mesecZaPrikaz(mesec: string): string {
  const [leto, m] = mesec.split('-');
  return `${m ?? ''}/${leto ?? ''}`;
}
